import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileText, Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { InvoiceExtractionResults, ExtractionResult } from "@/components/invoice/InvoiceExtractionResults";
import { resolvePaymentTerms, calculateDiscountAmount, enrichExtractionWithTerms } from "@prolarus/shared";
import { format, parseISO, isValid } from "date-fns";
import { getDefaultStatusId, useActiveStatusId } from "@/hooks/useDefaultStatus";
import { useAuth } from "@/contexts/AuthContext";
import { profileService, vendorService, invoiceService, invoiceItemService, type ServiceContext } from "@/services";
import { extractService } from "@/api/extract";

interface Vendor {
  id: string;
  name: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

type AIProvider = "openai";

export default function ExtractInvoice() {
  const [file, setFile] = useState<File | null>(null);
  const [aiProvider, setAIProvider] = useState<AIProvider>("openai");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [results, setResults] = useState<ExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Fetch active status for vendors
  const { data: activeVendorStatus } = useActiveStatusId("vendor");

  // Fetch vendors for the company
  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ["vendors", profile?.company_id, activeVendorStatus?.id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await vendorService.listActive(profile.company_id, activeVendorStatus?.id || null);
      if (result.error) throw result.error;
      return (result.data || []).map((v) => ({ id: v.id, name: v.name }));
    },
    enabled: !!profile?.company_id,
  });

  // Auto-match vendor when extraction completes
  useEffect(() => {
    if (results?.data?.vendor_name && vendors.length > 0) {
      const extractedName = results.data.vendor_name.toLowerCase().trim();
      const match = vendors.find(
        (v) => v.name.toLowerCase().includes(extractedName) || extractedName.includes(v.name.toLowerCase()),
      );
      if (match) {
        setSelectedVendorId(match.id);
      }
    }
  }, [results, vendors]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (file: File) => {
    setError(null);
    setResults(null);

    if (file.type !== "application/pdf") {
      setError("Please upload a PDF file");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    setFile(file);
  };

  const handleExtract = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setProcessingStatus("Reading PDF file...");

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix
          const base64Data = result.split(",")[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const providerLabel = "OpenAI";
      setProcessingStatus(`Extracting data with ${providerLabel}...`);

      const data = await extractService.extractInvoice<ExtractionResult>({ pdfBase64: base64, aiProvider });

      if (!data.success) {
        throw new Error(data.error || "Extraction failed");
      }

      setResults(enrichExtractionWithTerms(data));
      toast({
        title: "Extraction Complete",
        description: `Successfully extracted invoice data using ${data.extraction_method.replace("_", " ")} method with ${providerLabel}.`,
      });
    } catch (err) {
      console.error("Extraction error:", err);
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      toast({
        title: "Extraction Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProcessingStatus("");
    }
  };

  const handleSave = async () => {
    if (!results || !file || !profile?.company_id || !user?.id) return;

    setIsSaving(true);

    try {
      const companyId = profile.company_id;
      const extractedData = results.data;
      const context: ServiceContext = { companyId, userId: user.id };

      // Explicit AI-extracted due date takes precedence
      let dueDate: string | null = null;
      let discountExpiration: string | null = null;
      let discount: number | null = null;

      if (extractedData.due_date) {
        try {
          const parsed = parseISO(extractedData.due_date);
          if (isValid(parsed)) dueDate = extractedData.due_date;
        } catch { /* ignore */ }
      }

      if (extractedData.payment_terms && extractedData.invoice_date) {
        try {
          const invoiceDate = parseISO(extractedData.invoice_date);
          if (isValid(invoiceDate)) {
            const resolved = resolvePaymentTerms(extractedData.payment_terms, invoiceDate);
            discount = resolved.discountPercent || null;
            discountExpiration = format(resolved.discountExpirationDate, "yyyy-MM-dd");
            if (!dueDate) dueDate = format(resolved.dueDate, "yyyy-MM-dd");
          }
        } catch { /* terms didn't parse — OK for AI-extracted data */ }
      }

      const discountAmt = calculateDiscountAmount(extractedData.total_amount, discount);

      // Get default status_id for invoice
      const defaultStatusId = await getDefaultStatusId("invoice");

      // Create the invoice record using service
      const invoiceResult = await invoiceService.create(
        {
          invoice_number: extractedData.invoice_number,
          invoice_date: extractedData.invoice_date || null,
          due_date: dueDate,
          po_number: extractedData.po_number,
          payment_terms: extractedData.payment_terms,
          discount: discount,
          discount_amt: discountAmt,
          discount_expiration: discountExpiration,
          currency: extractedData.currency || "USD",
          subtotal: extractedData.subtotal || 0,
          tax_amount: extractedData.tax_amount || 0,
          total_amount: extractedData.total_amount || 0,
          status_id: defaultStatusId,
          extraction_method: results.extraction_method,
          extraction_confidence: results.confidence,
          ai_provider: aiProvider,
          source_filename: file.name,
          vendor_id: selectedVendorId,
        },
        context,
      );

      if (invoiceResult.error || !invoiceResult.data) {
        throw new Error(invoiceResult.error?.message || "Failed to create invoice");
      }

      const invoice = invoiceResult.data;

      // Upload PDF to storage
      const filePath = `${companyId}/${invoice.id}/${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("invoice-files")
        .upload(filePath, file, { contentType: "application/pdf" });

      if (uploadError) {
        console.error("File upload error:", uploadError);
        // Don't fail the whole operation, just log it
      } else {
        // Update invoice with file path using service
        await invoiceService.update(invoice.id, { source_file_path: filePath }, { source_file_path: null }, context);
      }

      // Insert line items using service
      if (extractedData.line_items?.length > 0) {
        const lineItems = extractedData.line_items.map((item, index) => ({
          invoice_id: invoice.id,
          sequence: item.sequence || index + 1,
          item_code: item.item_code,
          description: item.description || "No description",
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          uom: item.uom,
          discount: item.discount || null,
          line_total: item.line_total || 0,
        }));

        const itemsResult = await invoiceItemService.createBatch(lineItems, context);
        if (itemsResult.error) {
          console.error("Line items error:", itemsResult.error);
        }
      }

      toast({
        title: "Invoice Saved",
        description: `Invoice ${extractedData.invoice_number || invoice.id} has been saved.`,
      });

      // Navigate to invoices list
      navigate("/invoices");
    } catch (err) {
      console.error("Save error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to save invoice";
      toast({
        title: "Save Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setResults(null);
    setError(null);
    setSelectedVendorId(null);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Extract Invoice Data</h1>
        <p className="text-muted-foreground mt-1">
          Upload a PDF invoice to automatically extract vendor, line items, and totals using AI.
        </p>
      </div>

      {!results ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upload Invoice</CardTitle>
            <CardDescription>Supports native PDF invoices and scanned documents (max 10MB)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Drop Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${file ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
                ${isProcessing ? "pointer-events-none opacity-50" : "cursor-pointer"}
              `}
              onClick={() => !isProcessing && document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isProcessing}
              />

              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="w-12 h-12 text-primary" />
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <p className="font-medium">Drop PDF here or click to upload</p>
                  <p className="text-sm text-muted-foreground">Maximum file size: 10MB</p>
                </div>
              )}
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Processing Status */}
            {isProcessing && (
              <div className="flex items-center justify-center gap-3 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-muted-foreground">{processingStatus}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              {file && !isProcessing && (
                <Button variant="outline" onClick={handleClear}>
                  Clear
                </Button>
              )}
              <Button onClick={handleExtract} disabled={!file || isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Extract Data"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <InvoiceExtractionResults
          results={results}
          onClear={handleClear}
          onSave={handleSave}
          isSaving={isSaving}
          vendors={vendors}
          selectedVendorId={selectedVendorId}
          onVendorChange={setSelectedVendorId}
        />
      )}
    </div>
  );
}
